import { axiosRequest, METHOD } from '@/api/default.js';
import constants from '@/constants';

// [Epic1/#869c5bjxn] Publisher Admin API module
// Covers: material requests review, publisher management, admin guest links.
// All calls are admin-only — guarded by can_manage_publishers permission in router.
// Logging is handled by axiosRequest via requestName. Extra dev-only logs
// surface params/status without logging sensitive content.

function devLog(fnName, params) {
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info(`[publisherAdmin.${fnName}]`, params);
    }
}

/**
 * Get paginated list of material requests (admin view).
 * @param {Object} params
 * @param {string} [params.status]        - Filter by status (new|in_progress|done|rejected)
 * @param {number} [params.publisher_id]  - Filter by publisher
 * @param {number} [params.page]          - Page number (1-indexed, default 1)
 * @param {number} [params.per_page]      - Items per page (default 20)
 * @returns {Promise<Object>} { items: [], total: 0, page: 1, per_page: 20 }
 */
export async function getAdminMaterialRequests(params = {}) {
    devLog('getAdminMaterialRequests', {
        status: params.status,
        publisher_id: params.publisher_id,
        page: params.page,
    });
    const response = await axiosRequest({
        url: constants.URL.ADMIN_MATERIAL_REQUESTS,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_ADMIN_MATERIAL_REQUESTS,
        params,
        isLoader: false,
    });
    return response == null
        ? { items: [], total: 0, page: 1, per_page: 20 }
        : response;
}

/**
 * Update material request status and optionally set admin notes.
 * @param {number|string} requestId
 * @param {Object} data
 * @param {string} data.status        - New status (new|in_progress|done|rejected)
 * @param {string} [data.admin_notes] - Internal notes (not logged)
 * @returns {Promise<Object>} Updated request object
 */
export async function updateAdminMaterialRequestStatus(requestId, data) {
    devLog('updateAdminMaterialRequestStatus', {
        request_id: requestId,
        status: data.status,
    });
    const url = constants.URL.ADMIN_MATERIAL_REQUEST_UPDATE_STATUS.replace(
        '{request_id}',
        requestId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.PATCH,
        requestName:
            constants.API_REQUEST_NAMES.UPDATE_ADMIN_MATERIAL_REQUEST_STATUS,
        data,
        isLoader: true,
    });
    return response;
}

/**
 * Get a presigned download URL for a file attached to a material request.
 * @param {number|string} requestId
 * @param {number|string} fileId
 * @returns {Promise<{download_url: string, expires_at: string}>}
 */
export async function downloadAdminMaterialRequestFile(requestId, fileId) {
    devLog('downloadAdminMaterialRequestFile', {
        request_id: requestId,
        file_id: fileId,
    });
    const url = constants.URL.ADMIN_MATERIAL_REQUEST_DOWNLOAD_FILE
        .replace('{request_id}', requestId)
        .replace('{file_id}', fileId);
    const response = await axiosRequest({
        url,
        method: METHOD.GET,
        requestName:
            constants.API_REQUEST_NAMES.DOWNLOAD_ADMIN_MATERIAL_REQUEST_FILE,
        isLoader: false,
    });
    return response;
}

/**
 * Get paginated list of publishers (admin view).
 * @param {Object} params
 * @param {string} [params.search]    - Search by name/email
 * @param {string} [params.status]    - Filter by status (active|deleted|migrated)
 * @param {number} [params.page]      - Page number (1-indexed, default 1)
 * @param {number} [params.per_page]  - Items per page (default 20)
 * @returns {Promise<Object>} { items: [], total: 0, page: 1, per_page: 20 }
 */
export async function getAdminPublishers(params = {}) {
    devLog('getAdminPublishers', {
        search: params.search,
        status: params.status,
        page: params.page,
    });
    const response = await axiosRequest({
        url: constants.URL.ADMIN_PUBLISHERS,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_ADMIN_PUBLISHERS,
        params,
        isLoader: false,
    });
    return response == null
        ? { items: [], total: 0, page: 1, per_page: 20 }
        : response;
}

/**
 * Delete (deactivate) a publisher account.
 * Uses PATCH /admin/providers/{id}/status with { status: 'deleted' } —
 * the backend has no DELETE endpoint for providers.
 * @param {number} publisherId
 * @returns {Promise<Object>}
 */
export async function deleteAdminPublisher(publisherId) {
    devLog('deleteAdminPublisher', { publisher_id: publisherId });
    const url = constants.URL.ADMIN_PUBLISHER_STATUS.replace(
        '{publisher_id}',
        publisherId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.PATCH,
        requestName: constants.API_REQUEST_NAMES.DELETE_ADMIN_PUBLISHER,
        data: { status: 'deleted' },
        isLoader: true,
    });
    return response;
}

/**
 * Migrate a publisher's content to another provider.
 * @param {number} publisherId
 * @param {Object} data
 * @param {number} data.target_provider_id - ID of the target provider
 * @returns {Promise<Object>}
 */
export async function migrateAdminPublisher(publisherId, data) {
    devLog('migrateAdminPublisher', {
        publisher_id: publisherId,
        target_provider_id: data.target_provider_id,
    });
    const url = constants.URL.ADMIN_PUBLISHER_MIGRATE.replace(
        '{publisher_id}',
        publisherId
    );
    // [FIX] Backend expects PATCH /admin/providers/{id}/status with
    // { status: 'migrated', migration_target_provider_id } — not POST /migrate
    const response = await axiosRequest({
        url,
        method: METHOD.PATCH,
        requestName: constants.API_REQUEST_NAMES.MIGRATE_ADMIN_PUBLISHER,
        data: {
            status: 'migrated',
            migration_target_provider_id: data.target_provider_id,
        },
        isLoader: true,
    });
    return response;
}

/**
 * Get paginated list of guest links (admin view).
 * @param {Object} params
 * @param {number} [params.publisher_id] - Filter by publisher
 * @param {string} [params.status]       - Filter by link status
 * @param {number} [params.page]         - Page number (1-indexed, default 1)
 * @param {number} [params.per_page]     - Items per page (default 20)
 * @returns {Promise<Object>} { items: [], total: 0, page: 1, per_page: 20 }
 */
export async function getAdminGuestLinks(params = {}) {
    devLog('getAdminGuestLinks', {
        publisher_id: params.publisher_id,
        status: params.status,
        page: params.page,
    });
    const response = await axiosRequest({
        url: constants.URL.ADMIN_GUEST_LINKS,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_ADMIN_GUEST_LINKS,
        params,
        isLoader: false,
    });
    return response == null
        ? { items: [], total: 0, page: 1, per_page: 20 }
        : response;
}

/**
 * Get all active provider assignments for a user.
 * @param {number} userId
 * @returns {Promise<Array>} [{ provider_id, provider_name, role, is_active }, ...]
 */
export async function getUserProviderAssignments(userId) {
    devLog('getUserProviderAssignments', { user_id: userId });
    const url = constants.URL.ADMIN_USER_PROVIDER_ASSIGNMENTS.replace(
        '{user_id}',
        userId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_USER_PROVIDER_ASSIGNMENTS,
        isLoader: false,
    });
    return response == null ? [] : response;
}

/**
 * Assign a provider to a user (bypasses invite flow).
 * Idempotent: activates existing inactive record if present.
 * @param {number} userId
 * @param {Object} data
 * @param {number} data.provider_id
 * @param {string} [data.role]       - "member" (default) or "owner"
 * @param {boolean} [data.is_public] - Set provider portal visibility (true = public, false = hidden).
 *                                     Omit to leave the current value unchanged.
 * @returns {Promise<Object>} { provider_id, provider_name, role, is_active, is_public }
 */
export async function assignProviderToUser(userId, data) {
    devLog('assignProviderToUser', {
        user_id: userId,
        provider_id: data.provider_id,
        role: data.role,
        is_public: data.is_public,
    });
    const url = constants.URL.ADMIN_USER_PROVIDER_ASSIGNMENTS.replace(
        '{user_id}',
        userId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.POST,
        requestName: constants.API_REQUEST_NAMES.ASSIGN_PROVIDER_TO_USER,
        data,
        isLoader: true,
    });
    return response;
}

/**
 * Update provider portal visibility.
 * @param {number} providerId
 * @param {boolean} isPublic - true = public, false = hidden
 * @returns {Promise<Object>} Updated provider object
 */
export async function updateProviderVisibility(providerId, isPublic) {
    devLog('updateProviderVisibility', {
        provider_id: providerId,
        is_public: isPublic,
    });
    const url = constants.URL.ADMIN_PROVIDER_VISIBILITY.replace(
        '{provider_id}',
        providerId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.PATCH,
        requestName: constants.API_REQUEST_NAMES.UPDATE_PROVIDER_VISIBILITY,
        data: { is_public: isPublic },
        isLoader: false,
    });
    return response;
}

/**
 * Get all active team members for a provider.
 * @param {number} providerId
 * @returns {Promise<Array>} [{ user_id, user_name, user_email, role, is_active }, ...]
 */
export async function getProviderTeam(providerId) {
    devLog('getProviderTeam', { provider_id: providerId });
    const url = constants.URL.ADMIN_PROVIDER_TEAM.replace(
        '{provider_id}',
        providerId
    );
    const response = await axiosRequest({
        url,
        method: METHOD.GET,
        requestName: constants.API_REQUEST_NAMES.GET_PROVIDER_TEAM,
        isLoader: false,
    });
    return response == null ? [] : response;
}

/**
 * Remove (deactivate) a provider assignment from a user.
 * @param {number} userId
 * @param {number} providerId
 * @returns {Promise<void>}
 */
export async function removeProviderFromUser(userId, providerId) {
    devLog('removeProviderFromUser', {
        user_id: userId,
        provider_id: providerId,
    });
    const url = constants.URL.ADMIN_USER_PROVIDER_ASSIGNMENT_DELETE.replace(
        '{user_id}',
        userId
    ).replace('{provider_id}', providerId);
    await axiosRequest({
        url,
        method: METHOD.DELETE,
        requestName: constants.API_REQUEST_NAMES.REMOVE_PROVIDER_FROM_USER,
        isLoader: true,
    });
}
